import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Label,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import toast, { Toaster } from 'react-hot-toast';
import { useSede } from '../../../context/SedeContext';
import { useApi } from '../../../hooks/useApi';
import MedicalHistoryModal from './modals/MedicalHistoryModal';

const CrearAlumno = () => {
  const { selectedSedes, getSedeIds, getSelectionText, loading, availableSedes } = useSede();
  const api = useApi();
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    // Información personal
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    dpi: '',
    age: '',
    sex: '',
    address: '',
    
    // Información del usuario (se genera automáticamente)
    user_created: false,
    
    // Información médica
    has_medical_conditions: false,
    medical_history_data: null,
    notes: '',
    source: '',
    status: 'I', // Inactivo por defecto
    sede_id: selectedSedes.length > 0 ? selectedSedes[0].id : null,
    
    // Membresía y pago
    membership_id: '',
    payment_method: '',
    payment_amount: '',
    payment_date: '',
    payment_month: '',
    payment_year: new Date().getFullYear(),
    valid_until: '',
    trial_used: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingUsername, setIsGeneratingUsername] = useState(false);
  const [memberships, setMemberships] = useState([]);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createdClient, setCreatedClient] = useState(null);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [createdPayment, setCreatedPayment] = useState(null);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  
  // Modal de confirmación
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentToConfirm, setPaymentToConfirm] = useState(null);
  
  // Modal de historial médico
  const [showMedicalModal, setShowMedicalModal] = useState(false);

  // Función para manejar el guardado del historial médico
  const handleMedicalHistorySave = (medicalData) => {
    setFormData(prev => ({
      ...prev,
      medical_history_data: medicalData
    }));
    toast.success('Historial médico guardado exitosamente');
  };

  // Función para abrir el modal médico
  const openMedicalModal = () => {
    setShowMedicalModal(true);
  };

  // Función para cerrar el modal médico
  const closeMedicalModal = () => {
    setShowMedicalModal(false);
  };

  // Función para calcular la fecha de vencimiento hasta el final del mes seleccionado
  const calculateValidUntil = (month, year) => {
    if (!month || !year) return '';
    
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);
    
    // Calcular el último día del mes seleccionado
    let lastDayOfMonth;
    if (monthNum === 2) {
      // Verificar año bisiesto
      if (yearNum % 4 === 0 && (yearNum % 100 !== 0 || yearNum % 400 === 0)) {
        lastDayOfMonth = 29;
      } else {
        lastDayOfMonth = 28;
      }
    } else if (monthNum === 4 || monthNum === 6 || monthNum === 9 || monthNum === 11) {
      lastDayOfMonth = 30;
    } else {
      lastDayOfMonth = 31;
    }
    
    // Crear la fecha del último día del mes seleccionado
    const validUntilDate = new Date(yearNum, monthNum - 1, lastDayOfMonth);
    return validUntilDate.toISOString().split('T')[0];
  };

  // Función para calcular la fecha de inicio del Mes a Pagar
  const calculatePaymentStartDate = (month, year) => {
    if (!month || !year) return '';
    
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);
    
    // Crear la fecha del primer día del mes seleccionado
    const startDate = new Date(yearNum, monthNum - 1, 1);
    return startDate.toISOString().split('T')[0];
  };

  // Inicializar fechas por defecto
  useEffect(() => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    
    setFormData(prev => ({
      ...prev,
      payment_date: today.toISOString().split('T')[0],
      payment_month: currentMonth.toString(),
      payment_year: currentYear,
      valid_until: calculateValidUntil(currentMonth.toString(), currentYear)
    }));
  }, []);

  // Cargar membresías disponibles
  useEffect(() => {
    const loadMemberships = async () => {
      if (selectedSedes.length === 0) return;
      
      setLoadingMemberships(true);
      try {
        const response = await api.get('studio/memberships/', {
          params: { sede_ids: selectedSedes.map(s => s.id).join(',') }
        });
        setMemberships(response.data);
      } catch (error) {
        console.error('Error cargando membresías:', error);
        toast.error('Error al cargar las membresías disponibles');
      } finally {
        setLoadingMemberships(false);
      }
    };

    loadMemberships();
  }, [selectedSedes]); // ✅ Removido 'api' de las dependencias

  // Opciones para el formulario
  const sexOptions = [
    { value: '', label: 'Seleccionar...' },
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' },
    { value: 'O', label: 'Otro' }
  ];

  const statusOptions = [
    { value: 'I', label: 'Inactivo' },
    { value: 'A', label: 'Activo' }
  ];

  const paymentMethodOptions = [
    { value: '', label: 'Seleccionar método...' },
    { value: 'Efectivo', label: 'Efectivo' },
    { value: 'Transferencia', label: 'Transferencia' },
    { value: 'VisaLink', label: 'VisaLink' }
  ];

  const sourceOptions = [
    { value: '', label: 'Seleccionar...' },
    { value: 'Recomendación', label: 'Recomendación' },
    { value: 'Instagram', label: 'Instagram' },
    { value: 'Facebook', label: 'Facebook' },
    { value: 'Google', label: 'Google' },
    { value: 'Página web', label: 'Página web' },
    { value: 'Volante', label: 'Volante' },
    { value: 'Otro', label: 'Otro' }
  ];

  // Paso 1: Crear cliente
  const createClient = async () => {
    // Prevenir múltiples clicks
    if (isCreatingClient) {
      toast.error('Ya se está procesando la creación del cliente, espera...');
      return;
    }

    if (!validatePersonalInfo()) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setIsCreatingClient(true);
    try {
      const clientData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone || null,
        dpi: formData.dpi || null,
        age: formData.age ? parseInt(formData.age) : null,
        sex: formData.sex || null,
        address: formData.address || null,
        notes: formData.notes || null,
        source: formData.source || null,
        status: formData.status,
        sede_id: selectedSedes.length > 0 ? selectedSedes[0].id : null
      };

      const clientResponse = await api.post('accounts/clients/', clientData);
      const client = clientResponse.data;
      setCreatedClient(client);
      
      toast.success(`¡Cliente creado exitosamente! ID: ${client.id}`);
    } catch (error) {
      console.error('Error creating client:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.email) {
          setErrors(prev => ({ ...prev, email: errorData.email[0] }));
        }
        if (errorData.phone) {
          setErrors(prev => ({ ...prev, phone: errorData.phone[0] }));
        }
        if (errorData.dpi) {
          setErrors(prev => ({ ...prev, dpi: errorData.dpi[0] }));
        }
        toast.error('Error al crear el cliente. Revisa los campos marcados.');
      } else {
        toast.error('Error al crear el cliente');
      }
    } finally {
      setIsCreatingClient(false);
    }
  };

  // Paso 2: Crear usuario para el cliente
  const createUserForClient = async () => {
    // Prevenir múltiples clicks
    if (isCreatingUser) {
      toast.error('Ya se está procesando la creación del usuario, espera...');
      return;
    }

    if (!createdClient) {
      toast.error('Primero debes crear el cliente');
      return;
    }

    setIsCreatingUser(true);
    try {
      const response = await api.post('auth/generate-user-for-client/', {
        email: formData.email.trim().toLowerCase(),
        sede_id: formData.sede_id
      });
      
      const userData = response.data.user;
      setCreatedUser(userData);
      setFormData(prev => ({ ...prev, user_created: true }));
      
      toast.success(`¡Usuario creado exitosamente! Username: ${userData.username}. Se envió un email con las credenciales.`);
    } catch (error) {
      console.error('Error creating user:', error);
      
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Error al crear el usuario');
      }
    } finally {
      setIsCreatingUser(false);
    }
  };

  // Paso 3: Crear pago (si hay membresía)
  const createPayment = async () => {
    // Prevenir múltiples clicks
    if (isCreatingPayment) {
      toast.error('Ya se está procesando la creación del pago, espera...');
      return;
    }

    if (!createdClient) {
      toast.error('Primero debes crear el cliente');
      return;
    }

    if (!formData.membership_id || !formData.payment_method) {
      toast.error('Selecciona una membresía y método de pago');
      return;
    }

    // Mostrar modal de confirmación
    const selectedMembership = memberships.find(m => m.id === parseInt(formData.membership_id));
    setPaymentToConfirm({
      client: createdClient,
      membership: selectedMembership,
      amount: formData.payment_amount,
      payment_method: formData.payment_method,
      payment_date: formData.payment_date,
      payment_month: formData.payment_month,
      payment_year: formData.payment_year,
      valid_until: formData.valid_until
    });
    setShowPaymentModal(true);
  };

  // Función para confirmar el pago
  const confirmPayment = async () => {
    setIsCreatingPayment(true);
    setShowPaymentModal(false);
    try {
      const paymentData = {
        client_id: createdClient.id,
        membership_id: formData.membership_id,
        amount: formData.payment_amount,
        payment_method: formData.payment_method,
        date_paid: formData.payment_date ? new Date(formData.payment_date).toISOString() : new Date().toISOString(),
        valid_from: formData.payment_date ? formData.payment_date : new Date().toISOString().split('T')[0], // Usar fecha de pago real
        payment_month: formData.payment_month,
        payment_year: formData.payment_year,
        payment_start_date: calculatePaymentStartDate(formData.payment_month, formData.payment_year),
        valid_until: formData.valid_until,
        sede_id: selectedSedes.length > 0 ? selectedSedes[0].id : null
      };

      const paymentResponse = await api.post('studio/payments/', paymentData);
      const payment = paymentResponse.data;
      setCreatedPayment(payment);

      // Actualizar el cliente con la información de trial_used
      if (formData.trial_used) {
        await api.patch(`accounts/clients/${createdClient.id}/`, {
          trial_used: formData.trial_used
        });
      }

      toast.success(`¡Pago creado exitosamente! Monto: Q${payment.amount}`);
    } catch (error) {
      console.error('Error creating payment:', error);
      
      if (error.response?.data?.error === 'Ya existe un pago similar creado recientemente') {
        toast.error(`⚠️ ${error.response.data.message}`);
        console.log('Pago duplicado detectado:', error.response.data);
      } else if (error.response?.data?.errors) {
        toast.error('Error en los datos del pago');
      } else {
        toast.error('Error al crear el pago');
      }
    } finally {
      setIsCreatingPayment(false);
    }
  };

  // Manejar cambios en los inputs
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Limpiar errores cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    // Actualizar monto del pago cuando se seleccione una membresía
    if (name === 'membership_id') {
      const selectedMembership = memberships.find(m => m.id === parseInt(value));
      if (selectedMembership) {
        setFormData(prev => ({
          ...prev,
          membership_id: value,
          payment_amount: selectedMembership.price
        }));
      }
    }
    
    // Calcular fecha de vencimiento cuando cambie el Mes a Pagar
    if (name === 'payment_month') {
      const calculatedValidUntil = calculateValidUntil(value, formData.payment_year);
      setFormData(prev => ({
        ...prev,
        payment_month: value,
        valid_until: calculatedValidUntil
      }));
    }
    
    // Calcular fecha de vencimiento cuando cambie el año de pago
    if (name === 'payment_year') {
      const calculatedValidUntil = calculateValidUntil(formData.payment_month, value);
      setFormData(prev => ({
        ...prev,
        payment_year: value,
        valid_until: calculatedValidUntil
      }));
    }
    
    // Calcular fecha de vencimiento cuando cambie la fecha de pago
    if (name === 'payment_date' && value) {
      const paymentDate = new Date(value);
      const validUntil = new Date(paymentDate);
      validUntil.setMonth(validUntil.getMonth() + 1);
      
      setFormData(prev => ({
        ...prev,
        payment_date: value,
        valid_until: validUntil.toISOString().split('T')[0]
      }));
    }
  };

  // Validaciones para información personal
  const validatePersonalInfo = () => {
    const newErrors = {};

    // Validaciones requeridas
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'El nombre es requerido';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'El apellido es requerido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    // Validación de teléfono (opcional pero si se ingresa debe ser válido)
    if (formData.phone && !/^(\+?502[0-9]{8}|[0-9]{8})$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Formato de teléfono inválido para Guatemala (8 dígitos)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Limpiar todo el formulario
  const clearAll = () => {
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      dpi: '',
      age: '',
      sex: '',
      address: '',
      user_created: false,
      has_medical_conditions: false,
      medical_history_data: null,
      notes: '',
      source: '',
      status: 'I',
      sede_id: selectedSedes.length > 0 ? selectedSedes[0].id : null,
      membership_id: '',
      payment_method: '',
      payment_amount: '',
      payment_date: today.toISOString().split('T')[0],
      payment_month: currentMonth.toString(),
      payment_year: currentYear,
      valid_until: calculateValidUntil(currentMonth.toString(), currentYear),
      trial_used: false
    });
    
    setErrors({});
    setCreatedUser(null);
    setCreatedClient(null);
    setCreatedPayment(null);
  };

  // Mostrar loading mientras se cargan las sedes
  if (loading) {
    return (
      <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-3">Cargando sedes disponibles...</p>
        </div>
      </div>
    );
  }

  // Mostrar mensaje si no hay sedes disponibles
  if (!loading && availableSedes.length === 0) {
    return (
      <div className="content">
        <Row>
          <Col md="12">
            <Alert color="warning">
              <h4>No hay sedes disponibles</h4>
              <p>No se pueden crear alumnos sin sedes configuradas. Contacta al administrador.</p>
            </Alert>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <h4 className="title">Crear Nuevo Alumno</h4>
              <p className="category">
                Completa la información para crear un nuevo alumno y generar su cuenta de usuario
              </p>
              {selectedSedes.length > 0 && (
                <Alert color="info" className="mt-2">
                  <strong>Sede:</strong> {getSelectionText()}
                </Alert>
              )}
            </CardHeader>
            <CardBody>
              <Form>
                {/* Información Personal */}
                <h5 className="mb-3">Información Personal</h5>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Nombre *</Label>
                      <Input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        invalid={!!errors.first_name}
                        placeholder="Ingresa el nombre"
                      />
                      <FormFeedback>{errors.first_name}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Apellido *</Label>
                      <Input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        invalid={!!errors.last_name}
                        placeholder="Ingresa el apellido"
                      />
                      <FormFeedback>{errors.last_name}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        invalid={!!errors.email}
                        placeholder="correo@ejemplo.com"
                      />
                      <FormFeedback>{errors.email}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Teléfono</Label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        invalid={!!errors.phone}
                        placeholder="12345678"
                      />
                      <FormFeedback>{errors.phone}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label>DPI</Label>
                      <Input
                        type="text"
                        name="dpi"
                        value={formData.dpi}
                        onChange={handleInputChange}
                        invalid={!!errors.dpi}
                        placeholder="1234567890123"
                      />
                      <FormFeedback>{errors.dpi}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Edad</Label>
                      <Input
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleInputChange}
                        min="1"
                        max="120"
                        placeholder="25"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Sexo</Label>
                      <Input
                        type="select"
                        name="sex"
                        value={formData.sex}
                        onChange={handleInputChange}
                      >
                        {sexOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label>Dirección</Label>
                      <Input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Dirección completa"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Botón Crear Cliente */}
                <Row className="mt-4">
                  <Col md="12" className="text-center">
                    {createdClient ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <span className="text-success mr-2">✅ Cliente creado</span>
                        <small className="text-muted">ID: {createdClient.id}</small>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        color="primary"
                        outline="true"
                        size="lg"
                        onClick={createClient}
                        disabled={isCreatingClient}
                      >
                        {isCreatingClient ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Creando Cliente...
                          </>
                        ) : (
                          'Crear Cliente'
                        )}
                      </Button>
                    )}
                  </Col>
                </Row>

                {/* Información de Usuario */}
                <hr className="my-4" />
                <h5 className="mb-3">Información de Usuario</h5>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Estado del Cliente</Label>
                      <Input
                        type="select"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="12" className="text-center">
                    <FormGroup>
                      <Label>Cuenta de Usuario</Label>
                      <div className="d-flex align-items-center justify-content-center">
                        {formData.user_created ? (
                          <div className="d-flex align-items-center">
                            <span className="text-success mr-2">✅ Usuario creado</span>
                            {createdUser && (
                              <small className="text-muted">
                                Username: {createdUser.username}
                              </small>
                            )}
                          </div>
                        ) : createdClient ? (
                          <Button
                            type="button"
                            outline
                            color="success"
                            size="lg"
                            onClick={createUserForClient}
                            disabled={isCreatingUser}
                          >
                            {isCreatingUser ? (
                              <>
                                <Spinner size="sm" className="mr-2" />
                                Creando Usuario...
                              </>
                            ) : (
                              'Crear Usuario'
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted">
                            Primero crea el cliente para generar el usuario
                          </span>
                        )}
                      </div>
                      <small className="text-muted">
                        El usuario se crea automáticamente con el email y se envía un email con las credenciales
                      </small>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Información Médica */}
                <hr className="my-4" />
                <h5 className="mb-3">Información Adicional</h5>
                
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          name="has_medical_conditions"
                          checked={formData.has_medical_conditions}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        ¿El alumno tiene condiciones médicas que debamos conocer?
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                {formData.has_medical_conditions && (
                  <Row>
                    <Col md="12" className="text-center">
                      <Button
                        type="button"
                        color="info"
                        outline
                        size="lg"
                        onClick={openMedicalModal}
                        className="mb-3"
                      >
                        {formData.medical_history_data ? (
                          <>
                            ✅ Historial Médico Completado
                          </>
                        ) : (
                          <>
                            📋 Completar Historial Médico
                          </>
                        )}
                      </Button>
                      {formData.medical_history_data && (
                        <div className="mt-2">
                          <small className="text-success">
                            Historial médico guardado exitosamente
                          </small>
                        </div>
                      )}
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>¿Cómo nos conoció?</Label>
                      <Input
                        type="select"
                        name="source"
                        value={formData.source}
                        onChange={handleInputChange}
                      >
                        {sourceOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label>Notas</Label>
                      <Input
                        type="textarea"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Información adicional sobre el alumno..."
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Membresía y Pago */}
                <hr className="my-4" />
                <h5 className="mb-3">Membresía y Pago</h5>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Membresía</Label>
                      <Input
                        type="select"
                        name="membership_id"
                        value={formData.membership_id}
                        onChange={handleInputChange}
                        disabled={loadingMemberships}
                      >
                        <option value="">Seleccionar membresía...</option>
                        {memberships.map(membership => (
                          <option key={membership.id} value={membership.id}>
                            {membership.name} - Q{membership.price}
                          </option>
                        ))}
                      </Input>
                      {loadingMemberships && (
                        <small className="text-muted">Cargando membresías...</small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Método de Pago</Label>
                      <Input
                        type="select"
                        name="payment_method"
                        value={formData.payment_method}
                        onChange={handleInputChange}
                        disabled={!formData.membership_id}
                      >
                        {paymentMethodOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Monto del Pago</Label>
                      <Input
                        type="number"
                        name="payment_amount"
                        value={formData.payment_amount}
                        onChange={handleInputChange}
                        disabled={!formData.membership_id}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          name="trial_used"
                          checked={formData.trial_used}
                          onChange={handleInputChange}
                          className="mr-2"
                        />
                        ¿Ya usó su clase de prueba gratuita?
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Fecha de Pago</Label>
                      <Input
                        type="date"
                        name="payment_date"
                        value={formData.payment_date}
                        onChange={handleInputChange}
                        disabled={!formData.membership_id}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Válido Hasta</Label>
                      <Input
                        type="date"
                        name="valid_until"
                        value={formData.valid_until}
                        onChange={handleInputChange}
                        disabled={!formData.membership_id}
                        readOnly
                      />
                      <small className="text-muted">
                        Calculado automáticamente según el Mes a Pagar
                      </small>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Mes a Pagar</Label>
                      <Input
                        type="select"
                        name="payment_month"
                        value={formData.payment_month}
                        onChange={handleInputChange}
                        disabled={!formData.membership_id}
                      >
                        <option value="">Seleccionar mes</option>
                        <option value="1">Enero</option>
                        <option value="2">Febrero</option>
                        <option value="3">Marzo</option>
                        <option value="4">Abril</option>
                        <option value="5">Mayo</option>
                        <option value="6">Junio</option>
                        <option value="7">Julio</option>
                        <option value="8">Agosto</option>
                        <option value="9">Septiembre</option>
                        <option value="10">Octubre</option>
                        <option value="11">Noviembre</option>
                        <option value="12">Diciembre</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Año de Pago</Label>
                      <Input
                        type="number"
                        name="payment_year"
                        value={formData.payment_year}
                        onChange={handleInputChange}
                        disabled={!formData.membership_id}
                        min="2020"
                        max="2030"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {/* Botón Crear Pago */}
                <Row className="mt-4">
                  <Col md="12" className="text-center">
                    {createdPayment ? (
                      <div className="d-flex align-items-center justify-content-center">
                        <span className="text-success mr-2">✅ Pago creado</span>
                        <small className="text-muted">Monto: Q{createdPayment.amount}</small>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        color="warning"
                        outline
                        size="lg"
                        onClick={createPayment}
                        disabled={isCreatingPayment || !createdClient || !formData.membership_id || !formData.payment_method}
                      >
                        {isCreatingPayment ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Creando Pago...
                          </>
                        ) : (
                          'Crear Pago'
                        )}
                      </Button>
                    )}
                    {!createdClient && (
                      <div className="mt-2">
                        <small className="text-muted">Primero crea el cliente para poder crear el pago</small>
                      </div>
                    )}
                  </Col>
                </Row>

                {/* Botones */}
                <Row className="mt-4">
                  <Col md="12" className="text-center">
                    <Button
                      type="button"
                      color="primary"
                      outline
                      size="lg"
                      onClick={clearAll}
                    >
                      Limpiar Todo
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      
      {/* Modal de Confirmación de Pago */}
      <Modal isOpen={showPaymentModal} toggle={() => setShowPaymentModal(false)} size="lg">
        <ModalHeader toggle={() => setShowPaymentModal(false)}>
          <strong>⚠️ Confirmar Creación de Pago</strong>
        </ModalHeader>
        <ModalBody>
          {paymentToConfirm && (
            <div>
              <Alert color="warning" className="mb-3">
                <strong>Por favor, revisa cuidadosamente los datos antes de confirmar:</strong>
              </Alert>
              
              <Row>
                <Col md="6">
                  <strong>Cliente:</strong><br />
                  {paymentToConfirm.client.first_name} {paymentToConfirm.client.last_name}<br />
                  <small className="text-muted">ID: {paymentToConfirm.client.id}</small>
                </Col>
                <Col md="6">
                  <strong>Membresía:</strong><br />
                  {paymentToConfirm.membership?.name}<br />
                  <small className="text-muted">ID: {paymentToConfirm.membership?.id}</small>
                </Col>
              </Row>
              
              <hr />
              
              <Row>
                <Col md="6">
                  <strong>Monto:</strong><br />
                  Q{paymentToConfirm.amount}
                </Col>
                <Col md="6">
                  <strong>Método de Pago:</strong><br />
                  {paymentToConfirm.payment_method}
                </Col>
              </Row>
              
              <hr />
              
              <Row>
                <Col md="6">
                  <strong>Fecha de Pago:</strong><br />
                  {paymentToConfirm.payment_date ? new Date(paymentToConfirm.payment_date).toLocaleDateString() : 'Hoy'}
                </Col>
                <Col md="6">
                  <strong>Válido Hasta:</strong><br />
                  {paymentToConfirm.valid_until ? new Date(paymentToConfirm.valid_until).toLocaleDateString() : 'No especificado'}
                </Col>
              </Row>
              
              <Row>
                <Col md="6">
                  <strong>Mes a Pagar:</strong><br />
                  {paymentToConfirm.payment_month ? 
                    new Date(0, paymentToConfirm.payment_month - 1).toLocaleDateString('es', { month: 'long' }) : 
                    'No especificado'
                  }
                </Col>
                <Col md="6">
                  <strong>Año de Pago:</strong><br />
                  {paymentToConfirm.payment_year || 'No especificado'}
                </Col>
              </Row>
              
              <Alert color="info" className="mt-3">
                <small>
                  <strong>Nota:</strong> Una vez confirmado, este pago activará automáticamente al cliente 
                  y le asignará la membresía seleccionada.
                </small>
              </Alert>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowPaymentModal(false)}>
            Cancelar
          </Button>
          <Button 
            color="success" 
            onClick={confirmPayment}
            disabled={isCreatingPayment}
          >
            {isCreatingPayment ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Creando Pago...
              </>
            ) : (
              '✅ Confirmar y Crear Pago'
            )}
          </Button>
        </ModalFooter>
      </Modal>
      
      {/* Modal de Historial Médico */}
      <MedicalHistoryModal
        isOpen={showMedicalModal}
        toggle={closeMedicalModal}
        onSave={handleMedicalHistorySave}
      />
      
      <Toaster position="top-center" reverseOrder={false} />
    </div>
  );
};

export default CrearAlumno;
